import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { FiEdit2 } from "react-icons/fi";
import { FaSlack } from "react-icons/fa";
import { LuCalendarHeart } from "react-icons/lu";
import { Link as RouterLink } from "react-router";
import OutlineEmail from "components/Icon/Iconify/OutlineEmail";
import OutineLocation from "components/Icon/Iconify/OutlineLocation";
import GlobalTimezone from "components/Icon/Iconify/GlobalTimezone";
import LinkedInIcon from "components/Icon/Iconify/LinkedInIcon";
import { NewHireJourney } from "models/v2/newHireJourney";
import { format } from "date-fns";
import { Text14 } from "components/Text";
import { SecondaryButton } from "components/Button";
import AttributeRow from "components/DataDisplay/AttributeRow";
import UserUpdateForm from "components/Forms/UserUpdateForm";
import { userPathById } from "helpers/url";
import useCaminoStore from "hooks/useCaminoStore";

const UPDATE_FIELDS = [
  "firstName",
  "lastName",
  "startDate",
  "title",
  "preferredName",
  "pronouns",
  "personalEmail",
  "workEmail",
  "linkedinUrl",
  "location",
  "timezone",
  "department",
  "employmentType",
  "phoneNumber",
  "workMode",
  "onboardingPlanUrl",
] as const;

interface UserProfileDrawerProps {
  newHireJourney: NewHireJourney;
  isOpen: boolean;
  onClose: () => void;
  canEdit?: boolean;
}

export default function UserProfileDrawer({ newHireJourney, isOpen, onClose, canEdit = false }: UserProfileDrawerProps) {
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const company = useCaminoStore((state) => state.currentCompany);
  const { newHire, startDate } = newHireJourney;

  const formattedStartDate = startDate ? format(new Date(startDate), "MMM d, yyyy") : undefined;

  const hasSlackAccount = newHire?.workspaceAccountActive;

  const slackMessageUrl =
    company?.messageService && newHire?.messageServiceId
      ? new URL(`/team/${newHire.messageServiceId}`, company.messageService.url).toString()
      : undefined;

  const slackValue = hasSlackAccount ? (
    <HStack spacing={2}>
      <Text14 color="green.600">Synced</Text14>
      {slackMessageUrl && (
        <SecondaryButton
          as="a"
          href={slackMessageUrl}
          target="_blank"
          rel="noopener noreferrer"
          size="xs"
          height="20px"
        >
          Message
        </SecondaryButton>
      )}
    </HStack>
  ) : (
    <Text14 color="red.600">Not Found</Text14>
  );

  const linkedInValue = newHire?.linkedinUrl ? (
    <Text14
      as="a"
      href={newHire.linkedinUrl}
      target="_blank"
      rel="noopener noreferrer"
      color="blue.600"
      _hover={{ textDecoration: "underline" }}
    >
      View Profile
    </Text14>
  ) : undefined;

  return (
    <>
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader>
            <Flex justify="space-between" align="center">
              Profile Details
              <HStack spacing={2}>
                <Button as={RouterLink} to={userPathById(newHire?.id)} size="xs" variant="outline">
                  Go To User Page
                </Button>
                {canEdit && (
                  <Button
                    size="xs"
                    variant="outline"
                    leftIcon={<FiEdit2 />}
                    onClick={() => {
                      onClose();
                      onEditOpen();
                    }}
                  >
                    Edit
                  </Button>
                )}
              </HStack>
            </Flex>
          </DrawerHeader>
          <DrawerBody>
            <VStack align="stretch" spacing={4}>
              <AttributeRow icon={LuCalendarHeart} label="Start Date" value={formattedStartDate} />

              <AttributeRow icon={FaSlack} label="Slack Account" value={slackValue} />

              <AttributeRow icon={OutineLocation} label="Location" value={newHire?.location} />

              <AttributeRow icon={GlobalTimezone} label="Timezone" value={newHire?.timezone} />

              <AttributeRow icon={LinkedInIcon} label="LinkedIn" value={linkedInValue} />

              <AttributeRow icon={OutlineEmail} label="Personal Email" value={newHire?.personalEmail} copyable />

              <AttributeRow icon={OutlineEmail} label="Work Email" value={newHire?.workEmail} copyable />
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Drawer isOpen={isEditOpen} placement="right" onClose={onEditClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Update Profile</DrawerHeader>
          <DrawerBody>
            <UserUpdateForm
              width="100%"
              newHireJourneyId={newHireJourney.id}
              fields={[...UPDATE_FIELDS]}
              onSuccess={onEditClose}
              onClose={onEditClose}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
